<template>
  <el-menu
    router
    :collapse="collapse"
    :default-active="menuItem"
    :collapse-transition="false"
    @select="url => menuItem = url"
  >
    <template v-for="(item, index) in menuItems" :key="index">
      <!-- Группа меню -->
      <el-sub-menu v-if="isGroup(item)" :index="`group-${index}`">
        <template #title>
          <el-icon class="menu-item-icon">
            <component :is="getGroupIcon(item)" />
          </el-icon>
          <span>{{ label(item) }}</span>
        </template>
        <el-menu-item
          v-for="(subItem, subIndex) in item.items"
          :key="subIndex"
          :index="formatPath(subItem)"
        >
          <router-link :to="formatPath(subItem)" class="menu-item-link">
            <el-icon class="menu-item-icon">
              <component :is="getCachedViewIcon(subItem.route)" />
            </el-icon>
            <span class="menu-item-text">{{ label(subItem) }}</span>
          </router-link>
        </el-menu-item>
      </el-sub-menu>

      <!-- Обычный пункт меню -->
      <el-menu-item v-else :index="formatPath(item)">
        <el-tooltip v-if="collapse" :content="label(item)" placement="right">
          <router-link :to="formatPath(item)" class="menu-item-link">
            <el-icon class="menu-item-icon">
              <component :is="getCachedViewIcon(item.route)" />
            </el-icon>
          </router-link>
        </el-tooltip>
        <router-link v-else :to="formatPath(item)" class="menu-item-link">
          <el-icon class="menu-item-icon">
            <component :is="getCachedViewIcon(item.route)" />
          </el-icon>
          <span class="menu-item-text">{{ label(item) }}</span>
        </router-link>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue'
import {useRoute} from 'vue-router'
import {useLocale} from '@/composables/locale'
import {camelToKebab} from '@/helpers/tools'
import {useViewsStore} from '@/stores/views'
import {ElMenuItem, ElMenu, ElIcon, ElTooltip, ElSubMenu} from 'element-plus'
import {Folder} from '@element-plus/icons-vue'
import type {CatalogMenuRow, CatalogMenuGroup, CatalogMenuItem} from '@/types/View'
import {getCachedViewIcon} from '@/composables/menuIcons'
import {L2_MENU_GROUP} from '@/constants/l2Menu'
import {withProcessesMenuItem} from '@/constants/processesMenu'
import {useL2Identity} from '@/composables/useL2Identity'

defineProps<{collapse?: boolean}>()

const formatPath = (item: CatalogMenuRow) => item ? `/${camelToKebab(item.route)}` : '/'

const isGroup = (item: CatalogMenuItem): item is CatalogMenuGroup => {
  return 'type' in item && item.type === 'group'
}

const getGroupIcon = (item: CatalogMenuGroup) => {
  if (item.icon) {
    return getCachedViewIcon(item.icon)
  }
  return Folder
}

const route = useRoute()
const {label} = useLocale()
const viewsStore = useViewsStore()
const {isL2} = useL2Identity()

const VALIDATION_TASKS_MENU_ITEM: CatalogMenuRow = {
  route: 'validationTasks',
  labelRu: 'Уточнение данных',
  labelKk: 'Деректерді нақтылау',
}

// L2-меню захардкожено в [@/constants/l2Menu]. Для L2-юзера полностью заменяет
// обычное catalogmenu из БД — он видит ТОЛЬКО L2-разделы.
// Для всех остальных рендерится обычное меню из БД.
const menuItems = computed<CatalogMenuItem[]>(() => {
  if (isL2.value) return [L2_MENU_GROUP]
  return withProcessesMenuItem([...viewsStore.getCatalogMenu, VALIDATION_TASKS_MENU_ITEM])
})

const menuItem = ref(
  formatPath(
    menuItems.value
      .flatMap(item => isGroup(item) ? item.items : [item])
      .find(item => route.path.includes(formatPath(item)))!,
  ),
)
</script>

<style lang="scss">
.el-aside {
  overflow-x: hidden;
  .el-menu {
    width: 100%;
    overflow-x: hidden;
    border-right: none;
  }

  .el-menu-item {
    padding: 0 !important;
    padding-left: 0 !important;
    width: 100%;
    min-width: 0;
    overflow: hidden;

    display: flex;

    &.is-active > a {
      color: var(--el-menu-active-color);
    }

    .menu-item-link {
      padding: 0 16px;
      flex: 1;
      min-width: 0;
      width: 100%;
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--el-menu-text-color);
      text-decoration: none;
      box-sizing: border-box;
      overflow: hidden;

      .menu-item-icon {
        flex-shrink: 0;
        font-size: 18px;
      }

      .menu-item-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: calc(100% - 30px);
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    }
  }

  .el-sub-menu {
    .el-sub-menu__title {
      padding: 0 16px !important;

      .el-sub-menu__icon-arrow {
        right: 16px;
      }
    }

    .el-menu-item {
      padding-left: 0 !important;

      .menu-item-link {
        padding-left: 24px;
      }
    }
  }

  .el-menu--collapse {
    width: 100%;
    
    .el-menu-item .menu-item-link {
      justify-content: center;
      padding: 0 12px;
      gap: 0;
      min-width: 0;

      .menu-item-icon {
        font-size: 20px;
      }
    }
  }

  .el-menu .el-menu-item {
    transition: none !important;
    
    .menu-item-link {
      transition: none !important;
      
      .menu-item-text {
        transition: none !important;
        opacity: 1;
      }
      
      .menu-item-icon {
        transition: none !important;
      }
    }
  }
  
  .el-menu {
    transition: width 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
